import React from 'react';
import { Result as AntResult, ResultProps as AntResultProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface ResultProps extends AntResultProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  resultBase: {
    padding: theme.spacing[6],
    '& .ant-result-title': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      lineHeight: theme.typography.lineHeight.relaxed,
      marginTop: theme.spacing[2],
    },
    '& .ant-result-subtitle': {
      color: theme.color.semantic.text.secondary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      marginTop: theme.spacing[2],
      marginBottom: theme.spacing[4],
    },
    '& .ant-result-extra': {
      marginTop: theme.spacing[4],
    },
    '& .ant-result-content': {
      marginTop: theme.spacing[4],
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      padding: theme.spacing[4],
    },
  },
  resultSuccess: {
    '& .ant-result-icon': {
      '& .anticon': {
        color: theme.color.semantic.accent.green.strong,
      },
    },
  },
  resultError: {
    '& .ant-result-icon': {
      '& .anticon': {
        color: theme.color.semantic.brand.danger.primary,
      },
    },
  },
  resultInfo: {
    '& .ant-result-icon': {
      '& .anticon': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
  },
  resultWarning: {
    '& .ant-result-icon': {
      '& .anticon': {
        color: theme.color.semantic.accent.orange.strong,
      },
    },
  },
  result404: {
    '& .ant-result-icon': {
      '& .anticon': {
        color: theme.color.semantic.text.secondary,
      },
    },
  },
  result403: {
    '& .ant-result-icon': {
      '& .anticon': {
        color: theme.color.semantic.text.secondary,
      },
    },
  },
  result500: {
    '& .ant-result-icon': {
      '& .anticon': {
        color: theme.color.semantic.text.secondary,
      },
    },
  },
}));

const Result: React.FC<ResultProps> = ({
  status = 'info',
  className = '',
  ...rest
}) => {
  const classes = useStyles();

  const getStatusClass = () => {
    switch (status) {
      case 'success':
        return classes.resultSuccess;
      case 'error':
        return classes.resultError;
      case 'info':
        return classes.resultInfo;
      case 'warning':
        return classes.resultWarning;
      case '404':
        return classes.result404;
      case '403':
        return classes.result403;
      case '500':
        return classes.result500;
      default:
        return classes.resultInfo;
    }
  };

  const combinedClassName = `${classes.resultBase} ${getStatusClass()} ${className}`.trim();

  return (
    <AntResult
      status={status}
      {...rest}
      className={combinedClassName}
    />
  );
};

export default Result;
